import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  IconButton,
  Collapse,
  TextField,
  MenuItem,
  Stack,
  Button,
  alpha,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Settings as SettingsIcon,
  Language as LanguageIcon,
  DirectionsBoat as ShipIcon,
  Storage as DataIcon,
  Inventory2Outlined as ArchiveIcon,
} from "@mui/icons-material";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useSettings } from "../../Contexts/SettingsContext";
import TimezoneSelect from "./TimeZoneSelect";
import HourFormatSelect from "./HourFormatSelect";
import { useTranslation } from "react-i18next";
import DateFormatSelect from "./DateFormatSelect";
import {
  getShipName,
  getShipCallSign,
  getShipPumpCapacity,
  getShipDeadWeight,
} from "../../utils/domainSelectors";

const Settings = ({ onSelect }) => {
  const { shipData } = useShipSettings();
  const {
    timezone,
    setTimezone,
    is24Hour,
    setIs24Hour,
    language,
    setLanguage,
    dateFormat,
    setDateFormat,
  } = useSettings();
  const { t } = useTranslation("settings");

  const { currentVoyage } = useVoyageContextV2();
  const [formValues, setFormValues] = useState({
    shipName: "",
    callsign: "",
    pumpCapacity: "",
    deadWeight: "",
  });

  const [openShip, setOpenShip] = useState(false);
  const [openVoyage, setOpenVoyage] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    if (shipData) {
      setFormValues({
        shipName: getShipName(shipData),
        callsign: getShipCallSign(shipData),
        pumpCapacity: getShipPumpCapacity(shipData),
        deadWeight: getShipDeadWeight(shipData),
      });
    }
  }, [shipData]);

  // Gemensam stil för sektionsrubriker enligt "Maritime Dashboard"-konceptet
  const sectionLabelStyle = {
    display: "flex",
    alignItems: "center",
    color: "primary.main",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    mb: 2,
    fontSize: "0.85rem",
  };

  const paperStyle = {
    p: 3,
    mb: 3,
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    elevation: 0, // Enligt minnesregeln: låg elevation
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800, margin: "0 auto" }}>
      <Typography
        variant="h4"
        sx={{ mb: 4, fontWeight: 800, textAlign: "left" }}
      >
        {t("title")}
      </Typography>

      <Stack spacing={3}>
        {/* === SECTION: TIME & REGIONAL === */}
        <Paper sx={paperStyle} elevation={0}>
          <Typography variant="subtitle2" sx={sectionLabelStyle}>
            <SettingsIcon sx={{ mr: 1, fontSize: 20 }} /> {t("time")}
          </Typography>
          <Stack spacing={2.5}>
            <TimezoneSelect value={timezone} onChange={setTimezone} />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <HourFormatSelect value={is24Hour} onChange={setIs24Hour} />
              <DateFormatSelect value={dateFormat} onChange={setDateFormat} />
            </Box>
          </Stack>
        </Paper>

        <Paper sx={paperStyle} elevation={0}>
          <Typography variant="subtitle2" sx={sectionLabelStyle}>
            <ArchiveIcon sx={{ mr: 1, fontSize: 20 }} /> Local Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and download documents generated on this bridge computer.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={() => onSelect?.("documentArchive")}
          >
            Open Document Archive
          </Button>
        </Paper>

        {/* === SECTION: LANGUAGE === */}
        <Paper sx={paperStyle} elevation={0}>
          <Typography variant="subtitle2" sx={sectionLabelStyle}>
            <LanguageIcon sx={{ mr: 1, fontSize: 20 }} /> {t("language")}
          </Typography>
          <TextField
            select
            fullWidth
            label={t("language")}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            variant="outlined"
            size="small"
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="sv">Svenska</MenuItem>
          </TextField>
        </Paper>

        {/* === SECTION: SYSTEM DATA (JSON Viewers) === */}
        <Typography
          variant="subtitle2"
          sx={{ ...sectionLabelStyle, mt: 2, color: "text.secondary" }}
        >
          <DataIcon sx={{ mr: 1, fontSize: 20 }} /> System Diagnostics
        </Typography>

        {/* Ship Settings Debug */}
        {formValues.shipName && (
          <Paper
            sx={{
              ...paperStyle,
              bgcolor: alpha(theme.palette.action.hover, 0.3),
            }}
            elevation={0}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                cursor: "pointer",
                alignItems: "center",
              }}
              onClick={() => setOpenShip(!openShip)}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, display: "flex", alignItems: "center" }}
              >
                <ShipIcon
                  sx={{ mr: 1, fontSize: 18, color: "text.secondary" }}
                />{" "}
                {t("shipSettings")}
              </Typography>
              <IconButton size="small">
                {openShip ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={openShip}>
              <Box sx={{ mt: 2, position: "relative" }}>
                <pre
                  style={{
                    margin: 0,
                    padding: "16px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#000" : "#f5f5f5",
                    color:
                      theme.palette.mode === "dark" ? "#4caf50" : "#2e7d32", // Terminal-grön för data
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontFamily: "JetBrains Mono, Courier New, monospace",
                    overflowX: "auto",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {JSON.stringify(formValues, null, 2)}
                </pre>
              </Box>
            </Collapse>
          </Paper>
        )}

        {/* Current Voyage Debug */}
        {currentVoyage && (
          <Paper
            sx={{
              ...paperStyle,
              bgcolor: alpha(theme.palette.action.hover, 0.3),
            }}
            elevation={0}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                cursor: "pointer",
                alignItems: "center",
              }}
              onClick={() => setOpenVoyage(!openVoyage)}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, display: "flex", alignItems: "center" }}
              >
                <DataIcon
                  sx={{ mr: 1, fontSize: 18, color: "text.secondary" }}
                />{" "}
                {t("currentVoyage")}
              </Typography>
              <IconButton size="small">
                {openVoyage ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={openVoyage}>
              <Box sx={{ mt: 2 }}>
                <pre
                  style={{
                    margin: 0,
                    padding: "16px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#000" : "#f5f5f5",
                    color:
                      theme.palette.mode === "dark" ? "#4caf50" : "#2e7d32",
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontFamily: "JetBrains Mono, Courier New, monospace",
                    overflowX: "auto",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {JSON.stringify(currentVoyage, null, 2)}
                </pre>
              </Box>
            </Collapse>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default Settings;
